import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Link2, 
  Unlink, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle, 
  AlertTriangle,
  Loader2,
  FileText,
  Receipt,
  Building,
  Users,
  BookOpen,
  ArrowLeft,
  Clock,
  Info
} from 'lucide-react';
import GlassPanel from '../../components/GlassPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Xero logo SVG component
const XeroLogo = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="4" fill="#13B5EA"/>
    <path d="M7.5 8L12 12.5L16.5 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.5 16L12 11.5L16.5 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Connection states
const CONNECTION_STATES = {
  LOADING: 'loading',
  NOT_CONNECTED: 'not_connected',
  CREDENTIALS_MISSING: 'credentials_missing',
  CONNECTED: 'connected',
  TOKEN_EXPIRED: 'token_expired'
};

export function FinanceXero() {
  const [searchParams] = useSearchParams();
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.LOADING);
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  const getOrgId = useCallback(() => {
    return localStorage.getItem('org_id') || localStorage.getItem('current_org_id');
  }, []);

  // Fetch status and summary
  const fetchStatus = useCallback(async () => {
    const orgId = getOrgId();
    const headers = getAuthHeaders();
    
    // If no token, show not connected
    if (!headers.Authorization || headers.Authorization === 'Bearer null') {
      setConnectionState(CONNECTION_STATES.NOT_CONNECTED);
      setError('Please log in to use Xero integration');
      return;
    }
    
    // If no orgId, still check if credentials are configured
    if (!orgId) {
      // Try to get general status - just check if backend is configured
      try {
        // We need an org_id for the status endpoint, so show appropriate state
        setConnectionState(CONNECTION_STATES.NOT_CONNECTED);
        setError('Please select an organization first');
        return;
      } catch {
        setConnectionState(CONNECTION_STATES.NOT_CONNECTED);
        return;
      }
    }

    try {
      const response = await fetch(`${API_URL}/api/xero/status?org_id=${orgId}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
        setError(null);
        
        // Determine connection state based on response
        if (data.requires_credentials) {
          setConnectionState(CONNECTION_STATES.CREDENTIALS_MISSING);
        } else if (data.requires_reconnect) {
          setConnectionState(CONNECTION_STATES.TOKEN_EXPIRED);
        } else if (data.connected) {
          setConnectionState(CONNECTION_STATES.CONNECTED);
          // Fetch summary if connected
          const summaryRes = await fetch(`${API_URL}/api/xero/data/summary?org_id=${orgId}`, { headers });
          if (summaryRes.ok) {
            setSummary(await summaryRes.json());
          }
        } else {
          setConnectionState(CONNECTION_STATES.NOT_CONNECTED);
        }
      } else if (response.status === 401) {
        setConnectionState(CONNECTION_STATES.NOT_CONNECTED);
        setError('Session expired. Please log in again.');
      } else {
        setConnectionState(CONNECTION_STATES.NOT_CONNECTED);
      }
    } catch (err) {
      console.error('Error fetching Xero status:', err);
      setError('Failed to check Xero connection status');
      setConnectionState(CONNECTION_STATES.NOT_CONNECTED);
    }
  }, [getOrgId, getAuthHeaders]);

  // Handle URL params from OAuth callback
  useEffect(() => {
    const success = searchParams.get('success');
    const tenant = searchParams.get('tenant');
    const errorParam = searchParams.get('error');
    const message = searchParams.get('message');

    if (success === 'true') {
      setSuccessMessage(`Successfully connected to ${tenant || 'Xero'}!`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } else if (errorParam) {
      setError(message?.replace(/\+/g, ' ') || `Connection failed: ${errorParam}`);
    }

    // Clear URL params
    if (success || errorParam) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    fetchStatus();
  }, [searchParams, fetchStatus]);

  // Handle connect
  const handleConnect = async () => {
    const orgId = getOrgId();
    if (!orgId) {
      setError('Organization not found. Please select an organization first.');
      return;
    }

    setConnecting(true);
    setError(null);

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/xero/connect-url?org_id=${orgId}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Xero authorization
        window.location.href = data.authorization_url;
      } else if (response.status === 409) {
        // Credentials not configured
        const err = await response.json();
        setError(err.detail?.message || 'Xero credentials not configured yet.');
        setConnectionState(CONNECTION_STATES.CREDENTIALS_MISSING);
        setConnecting(false);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to initiate Xero connection');
        setConnecting(false);
      }
    } catch (err) {
      setError('Failed to connect to Xero');
      setConnecting(false);
    }
  };

  // Handle disconnect
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Xero? Your synced data will be retained.')) {
      return;
    }

    const orgId = getOrgId();
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/xero/disconnect?org_id=${orgId}`, {
        method: 'POST',
        headers
      });

      if (response.ok) {
        setConnectionState(CONNECTION_STATES.NOT_CONNECTED);
        setStatus(null);
        setSummary(null);
        setSuccessMessage('Disconnected from Xero. Your synced data has been retained.');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError('Failed to disconnect from Xero');
      }
    } catch (err) {
      setError('Failed to disconnect from Xero');
    }
  };

  // Handle manual sync
  const handleSync = async () => {
    const orgId = getOrgId();
    setSyncing(true);
    setError(null);
    setSyncResult(null);

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/xero/sync?org_id=${orgId}`, {
        method: 'POST',
        headers
      });

      if (response.ok) {
        const result = await response.json();
        setSyncResult(result);
        // Refresh summary
        fetchStatus();
      } else if (response.status === 401) {
        setConnectionState(CONNECTION_STATES.TOKEN_EXPIRED);
        setError('Xero connection expired. Please reconnect.');
      } else {
        const err = await response.json();
        setError(err.detail || 'Sync failed');
      }
    } catch (err) {
      setError('Failed to sync Xero data');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  return (
    <div className="page-container" data-testid="finance-xero-page">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 'var(--space-xl)' }}>
        <Link to="/finance" className="btn btn-ghost" style={{ marginBottom: 'var(--space-md)' }}>
          <ArrowLeft size={16} />
          Back to Finance
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <XeroLogo size={40} />
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
              Xero Integration
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              Read-only • Advisory only
            </p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div style={{ 
          padding: 'var(--space-md)', 
          background: 'var(--status-success-bg)', 
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }} data-testid="success-message">
          <CheckCircle size={18} style={{ color: 'var(--status-success)' }} />
          <span style={{ color: 'var(--status-success)' }}>{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{ 
          padding: 'var(--space-md)', 
          background: 'var(--status-danger-bg)', 
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }} data-testid="error-message">
          <AlertCircle size={18} style={{ color: 'var(--status-danger)' }} />
          <span style={{ color: 'var(--status-danger)' }}>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {connectionState === CONNECTION_STATES.LOADING && (
        <GlassPanel>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-3xl)' }}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
          </div>
        </GlassPanel>
      )}

      {/* Credentials Missing State */}
      {connectionState === CONNECTION_STATES.CREDENTIALS_MISSING && (
        <GlassPanel data-testid="credentials-missing-panel">
          <div style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              margin: '0 auto var(--space-lg)',
              background: 'var(--status-warning-bg)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AlertTriangle size={40} style={{ color: 'var(--status-warning)' }} />
            </div>
            
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-sm)' }}>
              Setup Pending
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 'var(--space-lg)', maxWidth: '400px', margin: '0 auto var(--space-lg)' }}>
              Xero credentials are not yet configured. Please add the following environment variables to enable Xero integration:
            </p>
            
            <div style={{ 
              background: 'var(--glass-bg)', 
              borderRadius: 'var(--radius-md)', 
              padding: 'var(--space-lg)',
              textAlign: 'left',
              fontFamily: 'monospace',
              fontSize: '0.8125rem',
              color: 'var(--text-secondary)',
              marginBottom: 'var(--space-lg)'
            }}>
              <div>XERO_CLIENT_ID=your_client_id</div>
              <div>XERO_CLIENT_SECRET=your_client_secret</div>
              <div>XERO_REDIRECT_URI={'{BACKEND_URL}'}/api/xero/callback</div>
            </div>
            
            <p style={{ color: 'var(--text-dimmed)', fontSize: '0.75rem' }}>
              Register your app at <a href="https://developer.xero.com/app/manage" target="_blank" rel="noopener noreferrer" style={{ color: '#13B5EA' }}>developer.xero.com</a>
            </p>
          </div>
        </GlassPanel>
      )}

      {/* Not Connected State */}
      {connectionState === CONNECTION_STATES.NOT_CONNECTED && (
        <GlassPanel data-testid="not-connected-panel">
          <div style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              margin: '0 auto var(--space-lg)',
              background: 'var(--glass-bg)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <XeroLogo size={40} />
            </div>
            
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-sm)' }}>
              Connect to Xero
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 'var(--space-lg)', maxWidth: '400px', margin: '0 auto var(--space-lg)' }}>
              Sync your invoices, bills, bank transactions, contacts, and chart of accounts from Xero for a complete financial overview.
            </p>
            
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="btn btn-primary"
              style={{ minWidth: '200px' }}
              data-testid="xero-connect-btn"
            >
              {connecting ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Connecting...
                </>
              ) : (
                <>
                  <Link2 size={16} />
                  Connect to Xero
                </>
              )}
            </button>
            
            <div style={{ 
              marginTop: 'var(--space-lg)', 
              padding: 'var(--space-md)',
              background: 'rgba(19, 181, 234, 0.05)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-sm)',
              textAlign: 'left'
            }}>
              <Info size={16} style={{ color: '#13B5EA', flexShrink: 0, marginTop: '2px' }} />
              <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                This integration is read-only and advisory only. We will never modify your Xero data.
              </span>
            </div>
          </div>
        </GlassPanel>
      )}

      {/* Token Expired State */}
      {connectionState === CONNECTION_STATES.TOKEN_EXPIRED && (
        <GlassPanel data-testid="token-expired-panel">
          <div style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              margin: '0 auto var(--space-lg)',
              background: 'var(--status-warning-bg)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Clock size={40} style={{ color: 'var(--status-warning)' }} />
            </div>
            
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-sm)' }}>
              Connection Expired
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 'var(--space-lg)', maxWidth: '400px', margin: '0 auto var(--space-lg)' }}>
              Your Xero connection has expired. Please reconnect to continue syncing data.
            </p>
            
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="btn btn-primary"
              style={{ minWidth: '200px' }}
              data-testid="xero-reconnect-btn"
            >
              {connecting ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Reconnecting...
                </>
              ) : (
                <>
                  <RefreshCw size={16} />
                  Reconnect to Xero
                </>
              )}
            </button>
          </div>
        </GlassPanel>
      )}

      {/* Connected State */}
      {connectionState === CONNECTION_STATES.CONNECTED && (
        <>
          {/* Connection Info Panel */}
          <GlassPanel data-testid="connected-panel">
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 'var(--space-md)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  background: 'var(--status-success-bg)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle size={24} style={{ color: 'var(--status-success)' }} />
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '1rem' }}>
                    {status?.tenant_name || 'Connected to Xero'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                    <Clock size={12} />
                    Last synced: {formatDate(status?.last_sync_at || summary?.last_sync_at)}
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="btn btn-primary"
                  data-testid="xero-sync-btn"
                >
                  <RefreshCw size={16} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
                  {syncing ? 'Syncing...' : 'Manual Sync'}
                </button>
                <button
                  onClick={handleDisconnect}
                  className="btn btn-ghost"
                  style={{ color: 'var(--status-danger)' }}
                  data-testid="xero-disconnect-btn"
                >
                  <Unlink size={16} />
                  Disconnect
                </button>
              </div>
            </div>
          </GlassPanel>

          {/* Sync Result */}
          {syncResult && (
            <GlassPanel style={{ marginTop: 'var(--space-lg)' }} data-testid="sync-result-panel">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
                <CheckCircle size={18} style={{ color: 'var(--status-success)' }} />
                <span style={{ fontWeight: '600', color: 'var(--status-success)' }}>Sync Complete</span>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  {formatDate(syncResult.synced_at)}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 'var(--space-md)' }}>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>{syncResult.invoices_synced}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Invoices</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>{syncResult.bills_synced}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Bills</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>{syncResult.bank_transactions_synced}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Transactions</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>{syncResult.contacts_synced}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Contacts</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>{syncResult.accounts_synced}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Accounts</div>
                </div>
              </div>
              {syncResult.errors?.length > 0 && (
                <div style={{ marginTop: 'var(--space-md)', padding: 'var(--space-sm)', background: 'var(--status-warning-bg)', borderRadius: 'var(--radius-sm)' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--status-warning)' }}>
                    {syncResult.errors.map((err, i) => <div key={i}>{err}</div>)}
                  </div>
                </div>
              )}
            </GlassPanel>
          )}

          {/* Financial Summary */}
          {summary?.financials && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-lg)', marginTop: 'var(--space-lg)' }}>
              <GlassPanel data-testid="receivables-panel">
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-xs)' }}>
                  Outstanding Receivables
                </div>
                <div style={{ fontSize: '1.75rem', fontWeight: '600', color: 'var(--status-success)' }}>
                  {formatCurrency(summary.financials.outstanding_receivables)}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
                  from {formatCurrency(summary.financials.total_invoiced)} invoiced
                </div>
              </GlassPanel>
              
              <GlassPanel data-testid="payables-panel">
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-xs)' }}>
                  Outstanding Payables
                </div>
                <div style={{ fontSize: '1.75rem', fontWeight: '600', color: 'var(--status-danger)' }}>
                  {formatCurrency(summary.financials.outstanding_payables)}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
                  from {formatCurrency(summary.financials.total_bills)} bills
                </div>
              </GlassPanel>
            </div>
          )}

          {/* Data Counts */}
          {summary?.data_counts && (
            <GlassPanel style={{ marginTop: 'var(--space-lg)' }} data-testid="data-counts-panel">
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-md)' }}>
                Synced Data
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--space-md)' }}>
                <div style={{ textAlign: 'center', padding: 'var(--space-md)', background: 'var(--glass-bg)', borderRadius: 'var(--radius-md)' }}>
                  <FileText size={20} style={{ color: 'var(--accent-primary)', marginBottom: 'var(--space-xs)' }} />
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                    {summary.data_counts.invoices}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Invoices</div>
                </div>
                <div style={{ textAlign: 'center', padding: 'var(--space-md)', background: 'var(--glass-bg)', borderRadius: 'var(--radius-md)' }}>
                  <Receipt size={20} style={{ color: 'var(--accent-primary)', marginBottom: 'var(--space-xs)' }} />
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                    {summary.data_counts.bills}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Bills</div>
                </div>
                <div style={{ textAlign: 'center', padding: 'var(--space-md)', background: 'var(--glass-bg)', borderRadius: 'var(--radius-md)' }}>
                  <Building size={20} style={{ color: 'var(--accent-primary)', marginBottom: 'var(--space-xs)' }} />
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                    {summary.data_counts.bank_transactions}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Transactions</div>
                </div>
                <div style={{ textAlign: 'center', padding: 'var(--space-md)', background: 'var(--glass-bg)', borderRadius: 'var(--radius-md)' }}>
                  <Users size={20} style={{ color: 'var(--accent-primary)', marginBottom: 'var(--space-xs)' }} />
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                    {summary.data_counts.contacts}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Contacts</div>
                </div>
                <div style={{ textAlign: 'center', padding: 'var(--space-md)', background: 'var(--glass-bg)', borderRadius: 'var(--radius-md)' }}>
                  <BookOpen size={20} style={{ color: 'var(--accent-primary)', marginBottom: 'var(--space-xs)' }} />
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                    {summary.data_counts.accounts}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Accounts</div>
                </div>
              </div>
            </GlassPanel>
          )}

          {/* Source Label */}
          <div style={{ 
            marginTop: 'var(--space-lg)', 
            padding: 'var(--space-sm) var(--space-md)',
            background: 'rgba(19, 181, 234, 0.1)',
            borderRadius: 'var(--radius-md)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            fontSize: '0.75rem',
            color: '#13B5EA'
          }}>
            <XeroLogo size={16} />
            Source: Xero (read-only) • Advisory only
          </div>
        </>
      )}
    </div>
  );
}

export default FinanceXero;
